import styled, { keyframes } from "styled-components";

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 3px rgba(255, 255, 255, 0.3), 0 0 6px currentColor;
    filter: brightness(1);
  }
  50% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.4), 0 0 10px currentColor;
    filter: brightness(1.1);
  }
`;

export const ExternalTrackRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TrackHeader = styled.div<{
  hasMultipleStems?: boolean;
  bubbleColor?: string;
}>`
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0;
  gap: 8px;
  background: transparent;
  border-radius: 12px;

  .track-title-section {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: ${({ hasMultipleStems }) =>
      hasMultipleStems ? "pointer" : "default"};

    .caret-container {
      width: 16px;
      min-width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      color: ${({ theme }) => theme.editor?.textLight || "#fff"};
      opacity: 0;
      transition: opacity 0.15s ease-in-out;

      &.clickable {
        cursor: pointer;
      }

      &.hidden {
        visibility: hidden;
      }
    }

    &:hover .caret-container {
      opacity: 0.7;
    }

    &:hover .track-title .stem-count {
      opacity: 0.7;
    }
  }

  .caret-icon {
    transition: transform 0.2s ease-in-out;

    &.expanded {
      transform: rotate(90deg);
    }
  }

  .track-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: ${({ theme }) => theme.fontMedium};
    font-size: 12px;
    color: #979797;
    min-width: 96px;
    max-width: 96px;

    .track-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }

    .stem-count {
      margin-left: 4px;
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
      flex-shrink: 0;
    }
  }

  .stem-row-content {
    display: flex;
    align-items: center;
    flex: 1;
    height: 24px;
    padding: 0 2px;
    gap: 8px;
    background-color: ${({ theme }) => theme.backgroundTag || "#2a2a2a"};
    border-radius: 12px;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: #505050;
    }
  }

  .stem-info {
    display: flex;
    align-items: center;
    min-width: 25%;
    max-width: 25%;
    cursor: pointer;
    gap: 6px;
    padding: 2px 6px 2px 2px;
    border-radius: 10px;
    margin-left: -2px;
  }

  .stem-color-bubble {
    position: relative;
    width: 18px;
    height: 18px;
    min-width: 18px;
    background-color: ${({ bubbleColor }) => bubbleColor || "#C7D469"};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    .plus-icon {
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
    }
  }

  .stem-info:hover .stem-color-bubble .plus-icon {
    opacity: 1;
  }

  .instrument-name {
    font-family: ${({ theme }) => theme.fontMedium};
    font-size: 12px;
    color: ${({ theme }) => theme.editor?.textLight || "#fff"};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const StemsList = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0;
  gap: 6px;
`;

export const StemItem = styled.div<{ bubbleColor?: string }>`
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0;
  gap: 8px;
  background: transparent;
  border-radius: 12px;
  margin-left: 0;

  .track-title-spacer {
    min-width: 96px;
    max-width: 96px;
    margin-left: 24px;
  }

  .stem-row-content {
    display: flex;
    align-items: center;
    flex: 1;
    height: 24px;
    padding: 0 2px;
    gap: 8px;
    background-color: ${({ theme }) => theme.backgroundTag || "#2a2a2a"}99;
    border-radius: 12px;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: #505050;
    }
  }

  .stem-info {
    display: flex;
    align-items: center;
    min-width: 25%;
    max-width: 25%;
    cursor: pointer;
    gap: 6px;
    padding: 2px 6px 2px 2px;
    border-radius: 10px;
    margin-left: -2px;
  }

  .stem-color-bubble {
    position: relative;
    width: 18px;
    height: 18px;
    min-width: 18px;
    background-color: ${({ bubbleColor }) => bubbleColor || "#C7D469"};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    .plus-icon {
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
    }
  }

  .stem-info:hover .stem-color-bubble .plus-icon {
    opacity: 1;
  }

  .instrument-name {
    font-family: ${({ theme }) => theme.fontMedium};
    font-size: 12px;
    color: ${({ theme }) => theme.editor?.textLight || "#fff"};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const RegionTimeline = styled.div`
  flex: 1;
  position: relative;
  height: 20px;
  background-color: #444444;
  border-radius: 10px;
  overflow: visible;
  margin-right: 4px;

  .no-regions {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 10px;
    color: #666;
    font-style: italic;
  }
`;

export const RegionBlock = styled.div<{ color?: string; isPlaying?: boolean }>`
  position: absolute;
  top: 2px;
  bottom: 2px;
  height: 16px;
  background-color: ${({ color }) => color || "#C7D469"};
  border-radius: 8px;
  cursor: pointer;
  opacity: ${({ isPlaying }) => (isPlaying ? 1 : 0.7)};
  transition: all 0.15s ease-in-out;
  color: ${({ color }) => color || "#C7D469"};
  animation: ${({ isPlaying }) => (isPlaying ? glowPulse : "none")} 0.8s
    ease-in-out infinite;

  &:hover {
    opacity: 1;
    transform: scaleY(1.1);
  }

  &:active {
    transform: scaleY(0.95);
  }
`;
